import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { http, API_BASE } from "@/lib/api";
import { MapPin, ArrowLeft, Sparkle } from "@phosphor-icons/react";
import TrustBadges from "@/components/TrustBadges";
import ShareButton from "@/components/ShareButton";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

function abs(u) { return !u ? "" : u.startsWith("http") ? u : `${API_BASE}${u}`; }

const KIND_LABEL = {
  nature: "Nature", weather: "Weather", event: "Natural events",
  road: "Road conditions", incident: "Incidents",
};

// GeographicPage — dynamic /g/:country/:state?/:city? route that shows every
// approved observation for the drilled-down geography. Presents:
//   - Hero with breadcrumb & follow button
//   - Kind rails (nature / weather / event / road / incident)
//   - Recent grid
export default function GeographicPage() {
  const { country, state, city } = useParams();
  const { user, requireSignIn } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);

  const decode = (s) => s ? decodeURIComponent(s).replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "";
  const dCountry = decode(country), dState = decode(state), dCity = decode(city);

  useEffect(() => {
    const params = new URLSearchParams();
    if (dCountry) params.set("country", dCountry);
    if (dState) params.set("state", dState);
    if (dCity) params.set("city", dCity);
    params.set("limit", "80");
    setLoading(true);
    http.get(`/nature/observations?${params}`)
      .then((r) => setItems(r.data?.observations || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [dCountry, dState, dCity]);

  useEffect(() => {
    if (!user) { setIsFollowing(false); return; }
    http.get("/nature/follows").then((r) => {
      const target = dCity || dState || dCountry;
      const has = (r.data?.follows || []).some((f) => f.type === "location" && f.target === target);
      setIsFollowing(has);
    }).catch(() => {});
  }, [user, dCountry, dState, dCity]);

  const grouped = useMemo(() => {
    const g = { nature: [], weather: [], event: [], road: [], incident: [] };
    for (const o of items) {
      const k = o.category_kinds?.[0];
      if (g[k]) g[k].push(o);
    }
    return g;
  }, [items]);

  const target = dCity || dState || dCountry;
  const toggleFollow = async () => {
    if (!requireSignIn("follow this place")) return;
    try {
      if (isFollowing) {
        await http.delete(`/nature/follows?type=location&target=${encodeURIComponent(target)}`);
        setIsFollowing(false); toast.success("Unfollowed");
      } else {
        await http.post("/nature/follows", { type: "location", target, label: [dCity, dState, dCountry].filter(Boolean).join(", ") });
        setIsFollowing(true); toast.success(`Following ${target}`);
      }
    } catch { toast.error("Couldn't update follow"); }
  };

  const breadcrumb = (
    <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">
      <Link to="/observations" className="hover:text-[#151312]">Observations</Link>
      {dCountry && <> · <Link to={`/g/${country}`} className="hover:text-[#151312]">{dCountry}</Link></>}
      {dState && <> · <Link to={`/g/${country}/${state}`} className="hover:text-[#151312]">{dState}</Link></>}
      {dCity && <> · {dCity}</>}
    </div>
  );

  return (
    <main className="bg-[color:var(--np-bg,#F5EBDC)] text-[color:var(--np-text,#151312)] min-h-screen pt-32 md:pt-36 pb-32" data-testid="geo-page">
      <section className="max-w-[1400px] mx-auto px-5 md:px-10">
        {breadcrumb}
        <h1 className="mt-2 font-serif-display text-4xl md:text-6xl leading-[0.95] tracking-tighter">
          Nature in {target}
        </h1>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            onClick={toggleFollow}
            data-testid="geo-follow"
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase border ${
              isFollowing
                ? "bg-[#151312] text-[#F5EBDC] border-[#151312]"
                : "border-[color:var(--np-brand,#B85F2A)] text-[color:var(--np-brand,#B85F2A)] hover:bg-[color:var(--np-brand,#B85F2A)] hover:text-white"
            }`}
          >
            {isFollowing ? "Following" : "Follow location"}
          </button>
          <ShareButton title={`Nature in ${target}`} text={`Observations from ${target} on NaturePulse.`} shortKind="geo" shortId={target?.replace(/\s+/g,'-').toLowerCase()} variant="chip" testid="geo-share" />
          <span className="ml-auto font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[color:var(--np-muted,#3A3830)]">
            {loading ? "loading…" : `${items.length} observations`}
          </span>
        </div>
      </section>

      {loading ? (
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 mt-8 font-mono-tech text-[10px] tracking-widest text-[color:var(--np-muted,#3A3830)]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 mt-8 rounded-xl border border-dashed border-black/20 p-10 text-center">
          <Sparkle size={20} weight="fill" className="mx-auto text-[color:var(--np-brand,#B85F2A)] mb-2" />
          <div className="font-serif-display text-2xl">No approved observations here yet.</div>
          <div className="mt-1 text-sm text-[#3A3830]">Be the first — sign in and submit one.</div>
        </div>
      ) : (
        <>
          {Object.entries(grouped).map(([kind, list]) => list.length === 0 ? null : (
            <section key={kind} className="max-w-[1400px] mx-auto px-5 md:px-10 mt-10" data-testid={`geo-section-${kind}`}>
              <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-3">
                / {KIND_LABEL[kind]} · {list.length}
              </div>
              <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory -mx-2 px-2 pb-2">
                {list.slice(0, 12).map((o) => {
                  const cover = o.media?.[0]?.url;
                  return (
                    <Link
                      key={o.id}
                      to={`/observations/${o.id}`}
                      className="snap-start shrink-0 w-[76%] sm:w-[46%] md:w-[32%] lg:w-[24%] rounded-2xl overflow-hidden bg-white/70 border border-black/10 hover:border-[color:var(--np-brand,#B85F2A)]"
                    >
                      {cover && <div className="aspect-[4/3] bg-black/5 overflow-hidden"><img src={abs(cover)} alt={o.title} className="w-full h-full object-cover" loading="lazy" /></div>}
                      <div className="p-3">
                        <div className="font-serif-display text-base leading-tight line-clamp-2">{o.title}</div>
                        <div className="mt-1 text-xs text-[#3A3830] flex items-center gap-1 line-clamp-1">
                          <MapPin size={11} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />
                          {o.location?.display_name}
                        </div>
                        <TrustBadges labels={o.trust_labels} size="xs" className="mt-1" />
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>
          ))}
        </>
      )}
    </main>
  );
}
